import {
  getNewOrderDefaultState,
  newOrderDefaultState,
  NewOrderLsKeys,
  NewOrderScenario,
  TNewOrderForm,
} from "@/entities/new-order";
import {
  getParsedAttributes,
  getParsedDuplicateCountries,
} from "@/entities/new-order/utils";
import { ICampaignInfo } from "@/Models/ApiModels";
import { useLazyGetAppInfoQuery } from "@/Store/APIs/Apps.api";
import { useLazyGetCampaignInfoQuery } from "@/Store/APIs/Campaign.api";
import { useDebouncedCallback } from "@mantine/hooks";
import { useLocalStorage } from "@uidotdev/usehooks";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";

export function useDuplicateOrderForm() {
  const { id: campaignId } = useParams<{ id: string }>();
  const [isLoading, setIsLoading] = useState(true);
  const [hasDraft, setHasDraft] = useState(false);
  const [isDateSelected, setIsDateSelected] = useState(false);

  const [duplicateOrderDraft, setDuplicateOrderDraft] = useLocalStorage<
    Record<string, TNewOrderForm>
  >(NewOrderLsKeys.DUPLICATE_ORDER_DRAFT, {});

  const methods = useForm<TNewOrderForm>({
    defaultValues: newOrderDefaultState,
  });

  const { watch, setValue, getValues } = methods;

  const { dateStart, dateStop } = watch();

  const [orderInfo, setOrderInfo] = useState<ICampaignInfo | null>(null);

  const [getAppInfo] = useLazyGetAppInfoQuery();
  const [getOrderInfo] = useLazyGetCampaignInfoQuery();

  const getInitialFormState = async (order: ICampaignInfo) => {
    // Создаем свежую копию дефолтного состояния
    const formState: TNewOrderForm = getNewOrderDefaultState();

    const appInfo = await getAppInfo(order.app).unwrap();

    const attributes = getParsedAttributes(order.settings_message);

    return {
      ...formState,
      type: order.type,
      app: appInfo,
      attributesList: attributes,
      delayedStart: {
        enable: Boolean(order.delayed_start),
        value: null,
      },
    };
  };

  useEffect(() => {
    const initializeForm = async () => {
      try {
        if (!campaignId) {
          return;
        }

        // Проверяем, есть ли сохраненный черновик
        const existingDraft = duplicateOrderDraft[campaignId];

        if (existingDraft) {
          // Если есть черновик - загружаем его целиком
          setHasDraft(true);
          setIsDateSelected(true);

          // Получаем данные заказа для возможности пересчета стран при изменении дат
          const order = await getOrderInfo(campaignId).unwrap();
          setOrderInfo(order);

          methods.reset(
            { ...existingDraft, activeScenario: NewOrderScenario.duplicate },
            { keepDefaultValues: false },
          );
        } else {
          // Если черновика нет - загружаем только базовые данные
          const order = await getOrderInfo(campaignId).unwrap();
          setOrderInfo(order);

          if (!order) {
            return;
          }

          const baseFormState = await getInitialFormState(order);

          methods.reset(
            { ...baseFormState, activeScenario: NewOrderScenario.duplicate },
            { keepDefaultValues: false },
          );
        }
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeForm();
  }, []);

  // Эффект для отслеживания изменения дат и обновления списка стран
  useEffect(() => {
    if (!campaignId || !orderInfo) {
      return;
    }

    // Проверяем, что обе даты выбраны
    if (!dateStart || !dateStop) {
      return;
    }

    const draftDateStart = duplicateOrderDraft[campaignId]?.dateStart;
    const draftDateStop = duplicateOrderDraft[campaignId]?.dateStop;

    // Проверяем, что даты действительно изменились
    const datesChanged =
      draftDateStart !== dateStart || draftDateStop !== dateStop;

    if (datesChanged || !isDateSelected) {
      // Если это первый выбор дат, устанавливаем флаг
      if (!isDateSelected) {
        setIsDateSelected(true);
      }

      // Парсим страны с учетом выбранных дат
      const newCountriesList = getParsedDuplicateCountries(
        orderInfo,
        dateStart,
        dateStop,
      );

      setValue("countriesList", newCountriesList);
    }
  }, [dateStart, dateStop, orderInfo]);

  const onSave = useDebouncedCallback((data: TNewOrderForm) => {
    // Сохраняем в localStorage только если:
    // - пользователь уже выбрал даты
    // - или уже есть черновик (при обновлении существующего черновика)
    if (campaignId && (isDateSelected || hasDraft)) {
      setDuplicateOrderDraft({
        ...duplicateOrderDraft,
        [campaignId]: data,
      });
    }
  }, 500);

  // Эффект для автосохранения формы
  useEffect(() => {
    const subscription = watch(() => {
      const formData = getValues();
      onSave(formData);
    });

    return () => subscription.unsubscribe();
  }, [watch, isDateSelected, hasDraft]);

  const clearDraft = () => {
    if (!campaignId) {
      return;
    }

    const updatedDrafts = { ...duplicateOrderDraft };
    delete updatedDrafts[campaignId];
    setDuplicateOrderDraft(updatedDrafts);
  };

  const handleReset = async () => {
    if (!orderInfo) {
      return;
    }

    setIsLoading(true);

    const resetFormState = await getInitialFormState(orderInfo);

    // Сбрасываем флаги и черновик
    clearDraft();
    setIsDateSelected(false);
    setHasDraft(false);

    methods.reset(
      { ...resetFormState, activeScenario: NewOrderScenario.duplicate },
      { keepDefaultValues: false },
    );
    setIsLoading(false);
  };

  return {
    methods,
    handleReset,
    isLoading,
  };
}
